/**
 * Related products carousel.
 *
 * The <product-recommendations> custom element fetches its content via XHR
 * and replaces its own innerHTML, which means any <script> tag embedded in
 * the section template never executes. This file runs on the outer page and
 * watches for carousel tracks being inserted into the DOM.
 */
(function () {
  'use strict';

  // Local debounce so we don't depend on global.js's load order. The carousel
  // is initialized via a MutationObserver that can fire before any other
  // scripts have finished evaluating in some edge cases (theme editor preview,
  // instant-nav back-forward cache).
  function debounce(fn, wait) {
    let t;
    return function debounced() {
      clearTimeout(t);
      t = setTimeout(() => fn.call(this), wait);
    };
  }

  function stepSize(track) {
    const item = track.querySelector('.grid__item');
    if (!item) return track.clientWidth;
    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
    return item.getBoundingClientRect().width + gap;
  }

  function initCarousel(root) {
    if (!root || root.dataset.carouselInit === '1') return;
    const track = root.querySelector('[data-carousel-track]');
    const prev  = root.querySelector('[data-carousel-prev]');
    const next  = root.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;
    root.dataset.carouselInit = '1';

    function update() {
      const max = track.scrollWidth - track.clientWidth - 2;
      const needsCarousel = track.scrollWidth > track.clientWidth + 2;
      prev.hidden = !needsCarousel || track.scrollLeft <= 2;
      next.hidden = !needsCarousel || track.scrollLeft >= max;
    }

    prev.addEventListener('click', function () {
      track.scrollBy({ left: -stepSize(track), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      track.scrollBy({ left: stepSize(track), behavior: 'smooth' });
    });
    track.addEventListener('scroll', update, { passive: true });
    // Debounce resize — the bare update() reads scrollWidth + clientWidth +
    // item bounding-rect, which forces synchronous layout on every fire.
    // Drag-resizing the window at ~60Hz would thrash on every frame without
    // this.
    window.addEventListener('resize', debounce(update, 150), { passive: true });

    // Defer once so images and fonts settle before first measurement.
    requestAnimationFrame(update);
    setTimeout(update, 300);
  }

  function scanAndInit(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const carousels = scope.querySelectorAll('[data-related-carousel]');
    carousels.forEach(initCarousel);
  }

  // Initial pass (in case content is already there, e.g. theme editor).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanAndInit(document); });
  } else {
    scanAndInit(document);
  }

  // Watch for product-recommendations swapping in its content later.
  const observer = new MutationObserver(function (mutations) {
    for (let i = 0; i < mutations.length; i++) {
      const added = mutations[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-related-carousel]')) {
          initCarousel(node);
        } else if (node.querySelectorAll) {
          scanAndInit(node);
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
